import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useExercise } from './ExerciseContext';
import { useServiceUnavailable } from '../hooks/useServiceUnavailable';
import { WorkoutsByDate, WorkoutSet, Exercise } from '../types';
import { WorkoutEntryFormState } from '../components/workouts/WorkoutEntryDialog';
import { getDateKey } from '../utils';
import { WorkoutService } from '../services/WorkoutService';
import { ProgramApiService } from '../services/api/ProgramApiService';

interface WorkoutContextType {
  // Date/Calendar
  selectedDate: Date | null;
  setSelectedDate: (date: Date | null) => void;
  calendarMonth: Date;
  setCalendarMonth: (date: Date) => void;

  // Workout Data
  workouts: WorkoutsByDate;
  workoutDays: Set<string>;
  programDays: Set<string>;
  workoutLoading: boolean;
  workoutError: string | null;
  todaysWorkouts: WorkoutSet[];

  // Dialog State
  currentSetForDialog: WorkoutEntryFormState | null;
  setCurrentSetForDialog: (set: WorkoutEntryFormState | null) => void;
  deleteDialogOpen: boolean;
  setIdToDelete: number | null;
  isSavingSet: boolean;

  // Handlers
  handleAddSet: (formData: WorkoutEntryFormState) => Promise<void>;
  handleEditSetTrigger: (set: WorkoutSet) => void;
  handleSaveEditSet: (setId: number | undefined, formData: WorkoutEntryFormState) => Promise<void>;
  handleCancelEditSet: () => void;
  handleDeleteSet: (setId: number) => Promise<void>;
  handleOpenDeleteDialog: (setIdToDeleteParam: number | undefined) => void;
  handleConfirmDelete: () => Promise<void>;
  handleCancelDelete: () => void;
  handleDeleteWorkoutDetail: (detailId: number | undefined) => Promise<void>;
  handleQuickAddSet: (workoutId: number, reps: string, weight: string, series: number) => Promise<void>;
  handleQuickEditSet: (workoutId: number, setDetail: any, reps: string, weight: string, series: number) => Promise<void>;
  refreshWorkoutsForDate: (date: Date) => Promise<void>;
  refreshProgramDays: () => Promise<void>;
}

const WorkoutContext = createContext<WorkoutContextType | undefined>(undefined);

export const WorkoutProvider: React.FC<{
  children: React.ReactNode;
  onServiceUnavailable: () => void;
}> = ({ children, onServiceUnavailable }) => {
  const { idToken } = useAuth();
  const { exercises } = useExercise();
  const { handleError } = useServiceUnavailable(onServiceUnavailable);

  // Date/Calendar state
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());

  // Workout state
  const [workouts, setWorkouts] = useState<WorkoutsByDate>({});
  const [workoutDays, setWorkoutDays] = useState<Set<string>>(new Set());
  const [programDays, setProgramDays] = useState<Set<string>>(new Set());
  const [workoutLoading, setWorkoutLoading] = useState(false);
  const [workoutError, setWorkoutError] = useState<string | null>(null);

  // Dialog state
  const [currentSetForDialog, setCurrentSetForDialog] = useState<WorkoutEntryFormState | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [setIdToDelete, setSetIdToDelete] = useState<number | null>(null);
  const [isSavingSet, setIsSavingSet] = useState(false);

  // Derived values
  const dateKey = getDateKey(selectedDate);
  const todaysWorkouts = useMemo(() => workouts[dateKey] || [], [workouts, dateKey]);

  // Load workouts for selected date
  useEffect(() => {
    if (!selectedDate || !idToken) {
      setWorkouts(prev => ({ ...prev, [getDateKey(selectedDate)]: [] }));
      setWorkoutLoading(false);
      return;
    }
    const dateKey = getDateKey(selectedDate);

    const loadWorkoutsForDate = async () => {
      setWorkoutLoading(true);
      setWorkoutError(null);
      try {
        const data = await WorkoutService.fetchWorkoutsForDate(dateKey);
        setWorkouts((prev) => ({ ...prev, [dateKey]: Array.isArray(data) ? data : [] }));
      } catch (error: any) {
        console.error(`Failed to fetch workouts for date ${dateKey}:`, error);
        setWorkoutError('Failed to fetch workouts');
        setWorkouts(prev => ({ ...prev, [dateKey]: [] }));
        handleError(error);
      } finally {
        setWorkoutLoading(false);
      }
    };
    loadWorkoutsForDate();
  }, [selectedDate, idToken, handleError]);

  // Fetch workout days and program session days for calendar month
  useEffect(() => {
    if (!idToken) return;
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth() + 1;
    const paddedMonth = String(month).padStart(2, '0');

    Promise.all([
      WorkoutService.fetchWorkoutDates(year, month),
      ProgramApiService.getProgramSessionDates(`${year}-${paddedMonth}`).catch(() => [] as string[]),
    ]).then(([dates, programDates]) => {
      setWorkoutDays(new Set(dates));
      setProgramDays(new Set(programDates));
    }).catch((error: any) => {
      console.error("Failed to fetch workout dates:", error);
      handleError(error);
    });
  }, [calendarMonth, idToken, handleError]);

  const handleAddSet = useCallback(async (formData: WorkoutEntryFormState) => {
    if (!selectedDate) return;
    const dateKey = getDateKey(selectedDate);

    // Snapshot for rollback
    const previousWorkouts = workouts[dateKey] || [];
    const previousWorkoutDays = new Set(workoutDays);

    // Build optimistic WorkoutSet
    let optimisticSet: WorkoutSet;
    if (formData.workout_type === 'amrap') {
      optimisticSet = {
        id: -Date.now(),
        exercise: 'AMRAP',
        workout_type: 'amrap',
        amrap_time_seconds: parseInt(formData.amrap_time_minutes || '0') * 60,
        comment: formData.comment?.trim() || undefined,
        details: [],
        amrap_details: formData.amrap_details?.map(d => ({
          exercise_id: parseInt(d.exercise_id),
          exercise_name: exercises.find(e => e.id === parseInt(d.exercise_id))?.name || '',
          reps: parseInt(d.reps)
        })) || []
      };
    } else {
      const exerciseId = typeof formData.exercise === 'string' ? parseInt(formData.exercise, 10) : (formData.exercise as any)?.id;
      const exerciseName = exercises.find(e => e.id === exerciseId)?.name || '';
      optimisticSet = {
        id: -Date.now(),
        exercise: exerciseName,
        exercise_id: exerciseId,
        workout_type: 'traditional',
        comment: formData.comment?.trim() || undefined,
        details: formData.details.map(d => ({
          series: parseInt(d.series, 10),
          reps: parseInt(d.reps, 10),
          weight: parseFloat(d.weight),
          rest_time_seconds: d.rest_time_seconds && d.rest_time_seconds.trim() !== '' ? parseInt(d.rest_time_seconds, 10) : undefined,
          rir: d.rir && d.rir.trim() !== '' ? parseInt(d.rir, 10) : undefined
        }))
      };
    }

    // Apply optimistic update and close dialog immediately
    setWorkouts(prev => ({ ...prev, [dateKey]: [...(prev[dateKey] || []), optimisticSet] }));
    setWorkoutDays(prev => new Set(prev).add(dateKey));
    setCurrentSetForDialog(null);

    // Fire backend call in the background
    try {
      const res = await WorkoutService.AddWorkoutSet(dateKey, formData, setWorkoutError);
      if (res && res.ok) {
        // Silent reconciliation — replace optimistic data with server data
        try {
          const data = await WorkoutService.fetchWorkoutsForDate(dateKey);
          setWorkouts(prev => ({ ...prev, [dateKey]: data }));
        } catch (error: any) {
          handleError(error);
        }
      } else if (res) {
        // Backend rejected — rollback
        setWorkouts(prev => ({ ...prev, [dateKey]: previousWorkouts }));
        setWorkoutDays(previousWorkoutDays);
        setWorkoutError('Failed to add workout');
      }
    } catch (error: any) {
      // Network/server error — rollback
      setWorkouts(prev => ({ ...prev, [dateKey]: previousWorkouts }));
      setWorkoutDays(previousWorkoutDays);
      setWorkoutError('Failed to add workout due to an error.');
      handleError(error);
    }
  }, [selectedDate, workouts, workoutDays, exercises, handleError]);

  const handleEditSetTrigger = useCallback((set: WorkoutSet) => {
    const targetExercise = exercises.find(ex => ex.name === set.exercise || ex.id.toString() === set.exercise_id?.toString());

    // For traditional workouts, convert details to form state format
    const formattedDetails = (set.details || []).map(detail => ({
      id: detail.id,
      series: detail.series.toString(),
      reps: detail.reps.toString(),
      weight: detail.weight.toString(),
      rest_time_seconds: detail.rest_time_seconds !== null && detail.rest_time_seconds !== undefined ? detail.rest_time_seconds.toString() : '',
      rir: detail.rir !== null && detail.rir !== undefined ? detail.rir.toString() : ''
    }));

    // For AMRAP workouts, ensure we have proper amrap_details with fallback
    let formattedAmrapDetails: { exercise_id: string; exercise_name?: string; reps: string }[] = [];
    if (set.workout_type === 'amrap' && set.amrap_details && set.amrap_details.length > 0) {
      formattedAmrapDetails = set.amrap_details.map(detail => ({
        exercise_id: detail.exercise_id.toString(),
        exercise_name: detail.exercise_name || '',
        reps: detail.reps.toString()
      }));
    } else if (set.workout_type === 'amrap') {
      // Initialize with at least one empty exercise for new AMRAP workouts
      formattedAmrapDetails = [{ exercise_id: '', exercise_name: '', reps: '' }];
    }

    setCurrentSetForDialog({
      exercise: targetExercise ? targetExercise.id.toString() : '',
      comment: set.comment || '',
      details: formattedDetails,
      workout_type: set.workout_type || 'traditional',
      amrap_time_minutes: set.amrap_time_seconds ? Math.floor(set.amrap_time_seconds / 60).toString() : '',
      amrap_details: formattedAmrapDetails
    });
  }, [exercises]);

  const handleSaveEditSet = useCallback(async (setId: number | undefined, formData: WorkoutEntryFormState) => {
    if (!selectedDate) return;
    const dateKey = getDateKey(selectedDate);

    // Snapshot for rollback
    const previousWorkouts = workouts[dateKey] || [];

    // Build optimistic update
    const optimisticWorkouts = previousWorkouts.map(w => {
      if (formData.workout_type === 'amrap' && w.workout_type === 'amrap') {
        return {
          ...w,
          amrap_time_seconds: parseInt(formData.amrap_time_minutes || '0') * 60,
          comment: formData.comment.trim() || undefined,
          amrap_details: formData.amrap_details?.map(d => ({
            exercise_id: parseInt(d.exercise_id),
            exercise_name: exercises.find(e => e.id === parseInt(d.exercise_id))?.name || d.exercise_name || '',
            reps: parseInt(d.reps)
          })) || []
        };
      }
      if (w.id === setId && formData.workout_type !== 'amrap') {
        const exerciseInput = formData.exercise;
        let exerciseId: number | undefined;
        if (typeof exerciseInput === 'string' && !isNaN(parseInt(exerciseInput, 10))) {
          exerciseId = parseInt(exerciseInput, 10);
        } else if (typeof exerciseInput === 'object' && exerciseInput !== null && typeof (exerciseInput as Exercise).id === 'number') {
          exerciseId = (exerciseInput as Exercise).id;
        }
        return {
          ...w,
          exercise: exerciseId ? (exercises.find(e => e.id === exerciseId)?.name || w.exercise) : w.exercise,
          exercise_id: exerciseId || w.exercise_id,
          comment: formData.comment.trim() || undefined,
          details: formData.details.map(d => ({
            id: d.id,
            series: parseInt(d.series, 10),
            reps: parseInt(d.reps, 10),
            weight: parseFloat(d.weight),
            rest_time_seconds: d.rest_time_seconds && d.rest_time_seconds.trim() !== '' ? parseInt(d.rest_time_seconds, 10) : undefined,
            rir: d.rir && d.rir.trim() !== '' ? parseInt(d.rir, 10) : undefined
          }))
        };
      }
      return w;
    });

    // Apply optimistic update and close dialog immediately
    setWorkouts(prev => ({ ...prev, [dateKey]: optimisticWorkouts }));
    setCurrentSetForDialog(null);
    setIsSavingSet(false);

    // Fire backend call in the background
    try {
      let res;

      if (formData.workout_type === 'amrap') {
        const currentWorkout = previousWorkouts.find(w =>
          w.workout_type === 'amrap' &&
          w.amrap_time_seconds !== undefined
        );
        if (!currentWorkout || !currentWorkout.amrap_time_seconds) {
          throw new Error('Could not find AMRAP workout data');
        }

        res = await WorkoutService.updateAmrapWorkout(currentWorkout.amrap_time_seconds, {
          time_minutes: parseInt(formData.amrap_time_minutes || '0'),
          comment: formData.comment.trim() || null,
          amrap_details: formData.amrap_details?.map(detail => ({
            exercise_id: parseInt(detail.exercise_id),
            reps: parseInt(detail.reps)
          })) || []
        });
      } else {
        if (setId === undefined) {
          throw new Error('Traditional workout ID is required for editing');
        }

        const { exercise: exerciseInput, comment, details } = formData;
        const formattedDetails = details.map(detail => ({
          id: detail.id,
          series: parseInt(detail.series, 10),
          reps: parseInt(detail.reps, 10),
          weight: parseFloat(detail.weight),
          rest_time_seconds: detail.rest_time_seconds && detail.rest_time_seconds.trim() !== '' ? parseInt(detail.rest_time_seconds, 10) : null,
          rir: detail.rir && detail.rir.trim() !== '' ? parseInt(detail.rir, 10) : null
        }));

        let exerciseId: number | null = null;
        if (exerciseInput && exerciseInput !== '') {
          if (typeof exerciseInput === 'string' && !isNaN(parseInt(exerciseInput, 10))) {
            exerciseId = parseInt(exerciseInput, 10);
          } else if (typeof exerciseInput === 'object' && exerciseInput !== null && typeof (exerciseInput as Exercise).id === 'number') {
            exerciseId = (exerciseInput as Exercise).id;
          }
        }

        res = await WorkoutService.updateWorkoutSet(setId, {
          exerciseId,
          comment: comment.trim() || null,
          details: formattedDetails
        });
      }

      if (res.ok) {
        // Silent reconciliation
        try {
          const data = await WorkoutService.fetchWorkoutsForDate(dateKey);
          setWorkouts(prev => ({ ...prev, [dateKey]: data }));
        } catch (error: any) {
          handleError(error);
        }
      } else {
        // Rollback
        setWorkouts(prev => ({ ...prev, [dateKey]: previousWorkouts }));
        setWorkoutError('Failed to save workout set');
      }
    } catch (error: any) {
      // Rollback
      setWorkouts(prev => ({ ...prev, [dateKey]: previousWorkouts }));
      setWorkoutError('Failed to save workout set due to an error.');
      handleError(error);
    }
  }, [selectedDate, workouts, exercises, handleError]);

  const handleCancelEditSet = useCallback(() => {
    setCurrentSetForDialog(null);
  }, []);

  const handleDeleteSet = useCallback(async (setId: number) => {
    if (!selectedDate) return;
    const dateKey = getDateKey(selectedDate);

    // Snapshot for rollback
    const previousWorkouts = workouts[dateKey] || [];
    const previousWorkoutDays = new Set(workoutDays);

    // Apply optimistic delete
    const optimisticWorkouts = previousWorkouts.filter(w => w.id !== setId);
    setWorkouts(prev => ({ ...prev, [dateKey]: optimisticWorkouts }));
    if (optimisticWorkouts.length === 0) {
      setWorkoutDays(prev => {
        const next = new Set(prev);
        next.delete(dateKey);
        return next;
      });
    }

    // Fire backend call in the background
    try {
      const res = await WorkoutService.deleteWorkoutSet(setId);
      if (res.ok) {
        // Silent reconciliation
        try {
          const data = await WorkoutService.fetchWorkoutsForDate(dateKey);
          setWorkouts(prev => ({ ...prev, [dateKey]: data }));
        } catch (error: any) {
          handleError(error);
        }
      } else {
        // Rollback
        setWorkouts(prev => ({ ...prev, [dateKey]: previousWorkouts }));
        setWorkoutDays(previousWorkoutDays);
      }
    } catch (error: any) {
      // Rollback
      setWorkouts(prev => ({ ...prev, [dateKey]: previousWorkouts }));
      setWorkoutDays(previousWorkoutDays);
      setWorkoutError('Failed to delete workout set due to an error.');
      handleError(error);
    }
  }, [selectedDate, workouts, workoutDays, handleError]);

  const handleOpenDeleteDialog = useCallback((setIdToDeleteParam: number | undefined) => {
    if (setIdToDeleteParam !== undefined) {
      setSetIdToDelete(setIdToDeleteParam);
      setDeleteDialogOpen(true);
    }
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (setIdToDelete !== null) {
      await handleDeleteSet(setIdToDelete);
      setDeleteDialogOpen(false);
      setSetIdToDelete(null);
    }
  }, [setIdToDelete, handleDeleteSet]);

  const handleCancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setSetIdToDelete(null);
  }, []);

  const handleDeleteWorkoutDetail = useCallback(async (detailId: number | undefined) => {
    if (detailId === undefined || !selectedDate) return;
    const dateKey = getDateKey(selectedDate);

    // Snapshot for rollback
    const previousWorkouts = workouts[dateKey] || [];

    // Apply optimistic delete of the detail
    const optimisticWorkouts = previousWorkouts.map(w => ({
      ...w,
      details: w.details.filter(d => d.id !== detailId)
    }));
    setWorkouts(prev => ({ ...prev, [dateKey]: optimisticWorkouts }));

    // Fire backend call in the background
    try {
      const res = await WorkoutService.deleteWorkoutDetail(detailId);
      if (res.ok) {
        // Silent reconciliation
        try {
          const data = await WorkoutService.fetchWorkoutsForDate(dateKey);
          setWorkouts(prev => ({ ...prev, [dateKey]: data }));
        } catch (error: any) {
          handleError(error);
        }
      } else {
        // Rollback
        setWorkouts(prev => ({ ...prev, [dateKey]: previousWorkouts }));
      }
    } catch (error: any) {
      // Rollback
      setWorkouts(prev => ({ ...prev, [dateKey]: previousWorkouts }));
      setWorkoutError('Failed to delete workout detail due to an error.');
      handleError(error);
    }
  }, [selectedDate, workouts, handleError]);

  const handleQuickAddSet = useCallback(async (workoutId: number, reps: string, weight: string, series: number) => {
    if (!selectedDate) throw new Error('No date selected');
    const dateKey = getDateKey(selectedDate);

    const currentWorkout = todaysWorkouts.find(w => w.id === workoutId);
    if (!currentWorkout) throw new Error('Workout not found');

    // Snapshot for rollback
    const previousWorkouts = workouts[dateKey] || [];

    // Build optimistic update — append the new detail
    const newDetail = {
      series: series,
      reps: parseInt(reps, 10),
      weight: parseFloat(weight),
      rest_time_seconds: undefined as number | undefined,
      rir: undefined as number | undefined
    };
    const optimisticWorkouts = previousWorkouts.map(w =>
      w.id === workoutId ? { ...w, details: [...w.details, newDetail] } : w
    );
    setWorkouts(prev => ({ ...prev, [dateKey]: optimisticWorkouts }));

    // Prepare details for the API call
    const updatedDetails = [
      ...currentWorkout.details.map(detail => ({
        id: detail.id,
        series: detail.series,
        reps: detail.reps,
        weight: detail.weight,
        rest_time_seconds: detail.rest_time_seconds,
        rir: detail.rir
      })),
      { series, reps: parseInt(reps, 10), weight: parseFloat(weight), rest_time_seconds: null, rir: null }
    ];

    // Fire backend call in the background
    try {
      const res = await WorkoutService.updateWorkoutSet(workoutId, {
        exerciseId: currentWorkout.exercise_id,
        comment: currentWorkout.comment,
        details: updatedDetails
      });

      if (res.ok) {
        // Silent reconciliation
        const data = await WorkoutService.fetchWorkoutsForDate(dateKey);
        setWorkouts(prev => ({ ...prev, [dateKey]: data }));
      } else {
        // Rollback
        setWorkouts(prev => ({ ...prev, [dateKey]: previousWorkouts }));
        throw new Error('Failed to add set to workout');
      }
    } catch (error: any) {
      // Rollback (if not already rolled back)
      setWorkouts(prev => {
        const current = prev[dateKey];
        if (current !== previousWorkouts) return { ...prev, [dateKey]: previousWorkouts };
        return prev;
      });
      handleError(error);
      if (error.message !== 'Failed to add set to workout') {
        setWorkoutError('Failed to add set to workout due to an error.');
      }
      throw error;
    }
  }, [selectedDate, todaysWorkouts, workouts, handleError]);

  const handleQuickEditSet = useCallback(async (workoutId: number, setDetail: any, reps: string, weight: string, series: number) => {
    if (!selectedDate) throw new Error('No date selected');
    const dateKey = getDateKey(selectedDate);

    const currentWorkout = todaysWorkouts.find(w => w.id === workoutId);
    if (!currentWorkout) throw new Error('Workout not found');

    // Snapshot for rollback
    const previousWorkouts = workouts[dateKey] || [];

    // Build optimistic update — update the matching detail
    const optimisticWorkouts = previousWorkouts.map(w => {
      if (w.id !== workoutId) return w;
      return {
        ...w,
        details: w.details.map(d =>
          d.id === setDetail.id ? { ...d, series, reps: parseInt(reps, 10), weight: parseFloat(weight) } : d
        )
      };
    });
    setWorkouts(prev => ({ ...prev, [dateKey]: optimisticWorkouts }));

    // Prepare details for the API call
    const updatedDetails = currentWorkout.details.map(detail => {
      if (detail.id === setDetail.id) {
        return { ...detail, series, reps: parseInt(reps, 10), weight: parseFloat(weight) };
      }
      return detail;
    });

    // Fire backend call in the background
    try {
      const res = await WorkoutService.updateWorkoutSet(workoutId, {
        exerciseId: currentWorkout.exercise_id,
        comment: currentWorkout.comment,
        details: updatedDetails.map(detail => ({
          id: detail.id,
          series: detail.series,
          reps: detail.reps,
          weight: detail.weight,
          rest_time_seconds: detail.rest_time_seconds,
          rir: detail.rir
        }))
      });

      if (res.ok) {
        // Silent reconciliation
        const data = await WorkoutService.fetchWorkoutsForDate(dateKey);
        setWorkouts(prev => ({ ...prev, [dateKey]: data }));
      } else {
        // Rollback
        setWorkouts(prev => ({ ...prev, [dateKey]: previousWorkouts }));
        throw new Error('Failed to edit set');
      }
    } catch (error: any) {
      // Rollback (if not already rolled back)
      setWorkouts(prev => {
        const current = prev[dateKey];
        if (current !== previousWorkouts) return { ...prev, [dateKey]: previousWorkouts };
        return prev;
      });
      handleError(error);
      if (error.message !== 'Failed to edit set') {
        setWorkoutError('Failed to edit set due to an error.');
      }
      throw error;
    }
  }, [selectedDate, todaysWorkouts, workouts, handleError]);

  const refreshWorkoutsForDate = useCallback(async (date: Date) => {
    const dateKey = getDateKey(date);
    try {
      const data = await WorkoutService.fetchWorkoutsForDate(dateKey);
      setWorkouts(prev => ({ ...prev, [dateKey]: Array.isArray(data) ? data : [] }));

      // Also refresh workout days for the calendar month
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      WorkoutService.fetchWorkoutDates(year, month)
        .then((dates) => setWorkoutDays(new Set(dates)))
        .catch((error: any) => {
          console.error("Failed to refresh workout days:", error);
          handleError(error);
        });
    } catch (error: any) {
      console.error("Failed to refresh workouts for date:", error);
      handleError(error);
    }
  }, [handleError]);

  const refreshProgramDays = useCallback(async () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth() + 1;
    const paddedMonth = String(month).padStart(2, '0');
    try {
      const programDates = await ProgramApiService.getProgramSessionDates(`${year}-${paddedMonth}`);
      setProgramDays(new Set(programDates));
    } catch {
      // silently ignore — stale programDays is acceptable
    }
  }, [calendarMonth]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    selectedDate,
    setSelectedDate,
    calendarMonth,
    setCalendarMonth,
    workouts,
    workoutDays,
    programDays,
    workoutLoading,
    workoutError,
    todaysWorkouts,
    currentSetForDialog,
    setCurrentSetForDialog,
    deleteDialogOpen,
    setIdToDelete,
    isSavingSet,
    handleAddSet,
    handleEditSetTrigger,
    handleSaveEditSet,
    handleCancelEditSet,
    handleDeleteSet,
    handleOpenDeleteDialog,
    handleConfirmDelete,
    handleCancelDelete,
    handleDeleteWorkoutDetail,
    handleQuickAddSet,
    handleQuickEditSet,
    refreshWorkoutsForDate,
    refreshProgramDays,
  }), [
    selectedDate,
    calendarMonth,
    workouts,
    workoutDays,
    programDays,
    workoutLoading,
    workoutError,
    todaysWorkouts,
    currentSetForDialog,
    deleteDialogOpen,
    setIdToDelete,
    isSavingSet,
    handleAddSet,
    handleEditSetTrigger,
    handleSaveEditSet,
    handleCancelEditSet,
    handleDeleteSet,
    handleOpenDeleteDialog,
    handleConfirmDelete,
    handleCancelDelete,
    handleDeleteWorkoutDetail,
    handleQuickAddSet,
    handleQuickEditSet,
    refreshWorkoutsForDate,
    refreshProgramDays,
  ]);

  return (
    <WorkoutContext.Provider value={value}>
      {children}
    </WorkoutContext.Provider>
  );
};

export const useWorkout = () => {
  const context = useContext(WorkoutContext);
  if (context === undefined) {
    throw new Error('useWorkout must be used within WorkoutProvider');
  }
  return context;
};
